import { useCallback } from 'react'
import {
    useMutation,
    UseMutationOptions,
    UseMutationResult,
    useQueryClient,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import toast from 'react-hot-toast'
import { QUERY_KEYS, QueryKey } from '@/constants/reactQueryKeys'
import { useTranslation } from 'react-i18next'
interface MutationHandlerOptions<TData, TError, TVariables, TContext> {
    /**
     * Custom success message to display in toast
     * @example successMessage: 'User created successfully'
     */
    successMessage?: string
    /**
     * Custom error message to display in toast (fallback if error has no message)
     * @example errorMessage: 'Failed to create user'
     */
    errorMessage?: string
    /**
     * Custom success handler (has higher priority than successMessage)
     * @example onSuccess: (data) => { console.log(data); navigate('/users') }
     */
    onSuccess?: (data: TData, variables: TVariables, context: TContext) => void
    /**
     * Custom error handler (has higher priority than errorMessage)
     * @example onError: (error) => { console.error(error) }
     */
    onError?: (error: TError, variables: TVariables, context: TContext) => void
    /**
     * Whether to show success toast (default: true)
     */
    showSuccessToast?: boolean
    /**
     * Whether to show error toast (default: true)
     */
    showErrorToast?: boolean
    /**
     * React Query keys to invalidate/refetch after successful mutation
     * Uses 'contains' mode for pattern matching (flexible)
     *
     * REQUIRED: Must use constants from @/constants/reactQueryKeys (type-safe)
     *
     * @example reactQueryKeys: QUERY_KEYS.CONTRACTS
     * @example reactQueryKeys: [QUERY_KEYS.CONTRACTS, QUERY_KEYS.SERVICES]
     * @example reactQueryKeys: QUERY_KEY_COMBINATIONS.SERVICE_WITH_TICKET
     */
    reactQueryKeys: QueryKey | QueryKey[] | readonly QueryKey[]
}

/**
 * Extract error message from AxiosError or Error
 */
// Map HTTP status codes to readable i18n keys (matched with axios-error locale)
const HTTP_STATUS_MAP: Record<number, string> = {
    400: 'bad-request',
    401: 'unauthorized',
    403: 'forbidden',
    404: 'not-found',
    409: 'conflict',
    410: 'gone',
    422: 'unprocessable-entity',
    500: 'internal-server-error',
}

/**
 * Một số lỗi backend trả về dạng động `"<kebab-key>: <chi tiết>"` thay vì key thuần —
 * ví dụ `customer-is-locked: đang tranh chấp công nợ` (customers.middlewares.go).
 * Chuỗi đó không bao giờ khớp một key i18n nào nên sẽ hiện thô cho người dùng.
 *
 * Hàm này chỉ chạy khi tra key nguyên chuỗi ĐÃ thất bại, và chỉ nhận phần đầu là
 * kebab-case đúng quy ước error key của backend — nên không đụng tới các thông báo
 * lỗi bình thường có chứa dấu hai chấm.
 *
 * Trả về '' nếu không áp dụng được, để caller rơi tiếp xuống nhánh dự phòng.
 */
const KEBAB_ERROR_KEY = /^[a-z][a-z0-9-]*$/

export const translateKeyWithDetail = (
    message: string,
    translate: (key: string, options: { defaultValue: string }) => string
): string => {
    const separator = message.indexOf(': ')
    if (separator <= 0) return ''

    const key = message.slice(0, separator)
    if (!KEBAB_ERROR_KEY.test(key)) return ''

    const translatedKey = translate(key, { defaultValue: '' })
    if (!translatedKey) return ''

    const detail = message.slice(separator + 2).trim()
    return detail ? `${translatedKey} — ${detail}` : translatedKey
}

export const getErrorMessage = (error: unknown, fallback?: string): string => {
    // Check if it's an AxiosError with response data
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as AxiosError
        const data = axiosError.response?.data
        const status = axiosError.response?.status

        // Handle object response: { error_description: "..." } or { message: "..." }
        // error_description is the i18n kebab-case key (pkg/types/response.types.go).
        if (data && typeof data === 'object') {
            const obj = data as Record<string, unknown>
            const desc =
                obj.error_description ?? obj.message ?? obj.detail ?? obj.error
            if (typeof desc === 'string') return desc
        }

        // Handle plain string response body
        if (typeof data === 'string' && data.length > 0 && data.length < 200) {
            return data
        }

        // Fallback to readable status message instead of raw Axios message
        if (status && HTTP_STATUS_MAP[status]) {
            return HTTP_STATUS_MAP[status]
        }

        return fallback || axiosError.message || 'An error occurred'
    }

    // Check if it's a regular Error
    if (error instanceof Error) {
        return error.message || fallback || 'An error occurred'
    }

    return fallback || 'An error occurred'
}

/**
 * Custom hook wrapper around useMutation that provides standardized error and success handling
 *
 * @example Basic usage
 * ```tsx
 * const { mutateAsync, isPending } = useMutationWithHandler(
 *   {
 *     mutationFn: createUser,
 *   },
 *   {
 *     successMessage: 'User created successfully',
 *     errorMessage: 'Failed to create user',
 *   }
 * )
 * ```
 *
 * @example With custom handlers
 * ```tsx
 * const { mutateAsync } = useMutationWithHandler(
 *   {
 *     mutationFn: createUser,
 *   },
 *   {
 *     onSuccess: (data) => {
 *       toast.success('User created!')
 *       navigate(`/users/${data.id}`)
 *     },
 *     onError: (error) => {
 *       console.error('Failed:', error)
 *     },
 *   }
 * )
 * ```
 *
 * @example Disable toasts
 * ```tsx
 * const { mutateAsync } = useMutationWithHandler(
 *   { mutationFn: updateUser },
 *   {
 *     showSuccessToast: false,
 *     showErrorToast: false,
 *   }
 * )
 * ```
 *
 * @example Auto-refetch with query keys (RECOMMENDED: use constants)
 * ```tsx
 * import { QUERY_KEYS, QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'
 *
 * // Single key
 * const { mutateAsync } = useMutationWithHandler(
 *   { mutationFn: createContract },
 *   {
 *     successMessage: 'Contract created!',
 *     reactQueryKeys: QUERY_KEYS.CONTRACTS,
 *   }
 * )
 *
 * // Multiple keys (manual)
 * const { mutateAsync } = useMutationWithHandler(
 *   { mutationFn: updateCustomer },
 *   {
 *     reactQueryKeys: [QUERY_KEYS.CUSTOMERS, QUERY_KEYS.CONTRACTS],
 *   }
 * )
 *
 * // Multiple keys (using predefined combinations)
 * const { mutateAsync } = useMutationWithHandler(
 *   { mutationFn: registerService },
 *   {
 *     reactQueryKeys: QUERY_KEY_COMBINATIONS.SERVICE_WITH_TICKET,
 *   }
 * )
 * ```
 */
/**
 * Extended return type - mutateAsync is overridden to catch errors internally
 */
type UseMutationWithHandlerResult<TData, TError, TVariables, TContext> = Omit<
    UseMutationResult<TData, TError, TVariables, TContext>,
    'mutateAsync'
> & {
    /**
     * Safe mutateAsync that catches errors internally.
     * Prevents React error overlay in development mode.
     * Errors are still handled by onError callback (toast shown).
     * @returns undefined if error occurs, otherwise returns the mutation data
     */
    mutateAsync: (variables: TVariables) => Promise<TData | undefined>
    /**
     * Alias for mutateAsync (kept for backwards compatibility)
     * @deprecated Use mutateAsync instead
     */
    safeMutateAsync: (variables: TVariables) => Promise<TData | undefined>
}

export const useMutationWithHandler = <
    TData = unknown,
    TError = Error,
    TVariables = void,
    TContext = unknown,
>(
    options: UseMutationOptions<TData, TError, TVariables, TContext>,
    handlers?: MutationHandlerOptions<TData, TError, TVariables, TContext>
): UseMutationWithHandlerResult<TData, TError, TVariables, TContext> => {
    const { t } = useTranslation()
    const { t: tAxiosError } = useTranslation('axios-error')
    const queryClient = useQueryClient()

    const mutation = useMutation<TData, TError, TVariables, TContext>({
        ...options,
        onSuccess: (data, variables, context) => {
            // alway invalidate/refetch activities query
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.ACTIVITIES],
            })
            // Invalidate/refetch queries if reactQueryKeys provided
            if (handlers?.reactQueryKeys) {
                const keys = (
                    Array.isArray(handlers.reactQueryKeys)
                        ? [...handlers.reactQueryKeys] // Spread to handle readonly arrays
                        : [handlers.reactQueryKeys]
                ) as QueryKey[]

                // Loop through each key and use contains mode for pattern matching
                keys.forEach((pattern) => {
                    const allQueries = queryClient.getQueryCache().getAll()

                    const matchingQueries = allQueries.filter((query) => {
                        const queryKey = query.queryKey.join('-').toLowerCase()
                        const searchPattern = pattern.toLowerCase()
                        return queryKey.includes(searchPattern) // contains mode
                    })

                    matchingQueries.forEach((query) => {
                        queryClient.invalidateQueries({
                            queryKey: query.queryKey,
                        })
                    })

                    console.log(
                        `[useMutationWithHandler] Invalidated ${matchingQueries.length} queries matching pattern: "${pattern}"`
                    )
                })
            }
            // Show success toast if not disabled
            // - If successMessage provided → show it
            // - If no successMessage and no onSuccess → show default message
            // - If onSuccess provided but no successMessage → skip toast (user handles it)
            if (handlers?.showSuccessToast !== false) {
                if (handlers?.successMessage) {
                    toast.success(t(handlers.successMessage))
                } else if (!handlers?.onSuccess) {
                    toast.success(t('operation-success'))
                }
            }

            // Call custom onSuccess handler if provided
            if (handlers?.onSuccess) {
                handlers.onSuccess(data, variables, context)
            }
        },
        onError: (error, variables, context) => {
            // Show error toast if not disabled
            if (handlers?.showErrorToast !== false) {
                const message = getErrorMessage(error, handlers?.errorMessage)
                // Try axios-error namespace first, then fall back to default namespace
                const translated = tAxiosError(message, { defaultValue: '' })
                toast.error(
                    translated ||
                        translateKeyWithDetail(message, tAxiosError) ||
                        t(message)
                )
            }

            // Call custom onError handler if provided
            if (handlers?.onError) {
                handlers.onError(error, variables, context as TContext)
            }
        },
    })

    /**
     * Override mutateAsync to catch errors internally.
     * This prevents React error overlay in development mode.
     * Errors are still handled by onError callback (toast shown).
     *
     * @returns undefined if error occurs, otherwise returns the mutation data
     */
    const safeMutateAsync = useCallback(
        async (variables: TVariables): Promise<TData | undefined> => {
            try {
                return await mutation.mutateAsync(variables)
            } catch {
                // Error already handled by onError callback
                return undefined
            }
        },
        [mutation]
    )

    return {
        ...mutation,
        // Override mutateAsync with safe version - no need to update existing code
        mutateAsync: safeMutateAsync,
        safeMutateAsync,
    }
}
